import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Checkbox } from '../../components/ui/checkbox';
import { Switch } from '../../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { employerAPI, utilityAPI, paymentsAPI } from '../../services/api';
import { HiringComplianceModal, useComplianceState } from '../../components/common/ComplianceComponents';
import VerificationRequiredModal from '../../components/common/VerificationRequiredModal';
import { VettScoreBadge } from '../../components/common/VettScoreBar';
import { useAuth } from '../../contexts/AuthContext';
import { useCompare } from '../../contexts/CompareContext';
import { toast } from 'sonner';
import { 
  Search as SearchIcon, User, MapPin, Briefcase, Bookmark, 
  Loader2, Lock, CheckCircle, Eye, Crown, Star, Shield, BadgeCheck, GitCompare
} from 'lucide-react';

const Search = ({ isFounderMode = false }) => {
  const navigate = useNavigate();
  const { emailVerified } = useAuth();
  const { toggleCandidate, isSelected, canAddMore } = useCompare();
  const [profile, setProfile] = useState(null);
  const [industries, setIndustries] = useState([]);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searching, setSearching] = useState(false);
  const [showVerificationModal, setShowVerificationModal] = useState(false);
  
  const {
    showHiringModal,
    acknowledgeHiringCompliance,
    checkHiringCompliance
  } = useComplianceState(profile);

  // Initialize filters from sessionStorage or default values
  const [filters, setFilters] = useState(() => {
    const saved = sessionStorage.getItem('vett_search_filters');
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (e) {
        return {
          keyword: '',
          industry: '',
          location: '',
          skills: '',
          rehire_yes: false,
          rehire_conditional: false,
          open_only: true,
          verified_only: false,
          tier_filter: [],
        };
      }
    }
    return {
      keyword: '',
      industry: '',
      location: '',
      skills: '',
      rehire_yes: false,
      rehire_conditional: false,
      open_only: true,
      verified_only: false,
      tier_filter: [], // Array of tier names: ['Verified', 'Established', 'Distinguished']
    };
  });
  const [vettIdSearch, setVettIdSearch] = useState('');

  // Restore results from sessionStorage on mount
  useEffect(() => {
    const savedResults = sessionStorage.getItem('vett_search_results');
    if (savedResults) {
      try {
        setResults(JSON.parse(savedResults));
      } catch (e) {
        // ignore
      }
    }
  }, []);

  // Save filters to sessionStorage when they change
  useEffect(() => {
    sessionStorage.setItem('vett_search_filters', JSON.stringify(filters));
  }, [filters]);

  // Save results to sessionStorage when they change
  useEffect(() => {
    if (results.length > 0) {
      sessionStorage.setItem('vett_search_results', JSON.stringify(results));
    }
  }, [results]);

  useEffect(() => {
    fetchInitialData();
  }, []);

  const fetchInitialData = async () => {
    try {
      const [profileRes, industriesRes] = await Promise.all([
        employerAPI.getProfile(),
        utilityAPI.getIndustries()
      ]);
      setProfile(profileRes.data);
      setIndustries(industriesRes.data);
    } catch (error) {
      console.error('Error fetching initial data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleVettIdSearch = async () => {
    if (!vettIdSearch.trim()) {
      toast.error('Enter a VETT ID');
      return;
    }
    
    if (!emailVerified) {
      setShowVerificationModal(true);
      return;
    }
    
    setSearching(true);
    try {
      const res = await employerAPI.searchByVettId(vettIdSearch.trim().toUpperCase());
      if (res.data && res.data.length > 0) {
        const profile = res.data[0];
        navigate(`/employer/talent/${profile.user_id}`);
      } else {
        toast.error('VETT ID not found or profile is private');
      }
    } catch (error) {
      if (error.response?.status === 404) {
        toast.error('VETT ID not found');
      } else {
        toast.error('Search failed');
      }
    } finally {
      setSearching(false);
    }
  };

  const handleSearch = async () => {
    // Check email verification first
    if (!emailVerified) {
      setShowVerificationModal(true);
      return;
    }
    
    if (profile?.subscription_tier === 'free' && !profile?.trial_active) {
      toast.error('Upgrade to access talent search');
      return;
    }

    setSearching(true);
    try {
      const res = await employerAPI.searchTalent({
        keyword: filters.keyword || undefined,
        industry: filters.industry || undefined,
        location: filters.location || undefined,
        skills: filters.skills || undefined,
        rehire_yes: filters.rehire_yes || undefined,
        rehire_conditional: filters.rehire_conditional || undefined,
        open_only: filters.open_only || undefined,
        verified_only: filters.verified_only || undefined,
        tier_filter: filters.tier_filter?.length > 0 ? filters.tier_filter.join(',') : undefined,
      });
      setResults(res.data);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Upgrade to access talent search');
      } else {
        toast.error('Search failed');
      }
    } finally {
      setSearching(false);
    }
  };

  const handleViewTalent = (employeeId) => {
    // Check hiring compliance before viewing talent
    if (checkHiringCompliance()) {
      navigate(`/employer/talent/${employeeId}`);
    }
  };

  const handleHiringAcknowledge = async () => {
    await acknowledgeHiringCompliance();
    setProfile(prev => ({ ...prev, hiring_compliance_acknowledged_at: new Date().toISOString() }));
  };

  const handleSaveCandidate = async (employeeId, e) => {
    e.stopPropagation();
    try {
      const res = await employerAPI.saveCandidate(employeeId);
      toast.success(res.data.saved ? 'Candidate saved' : 'Candidate unsaved');
    } catch (error) {
      toast.error('Failed to save candidate');
    }
  };

  const handleUpgrade = async (planId) => {
    try {
      const res = await paymentsAPI.createCheckout(planId, window.location.origin);
      window.location.href = res.data.url;
    } catch (error) {
      toast.error('Failed to start checkout');
    }
  };

  if (loading) {
    const content = (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-[#1B5AFF]" />
      </div>
    );
    return isFounderMode ? content : <DashboardLayout>{content}</DashboardLayout>;
  }

  const isPaid = profile?.subscription_tier && (
    profile.subscription_tier !== 'free' || profile.trial_active
  );

  const searchContent = (
    <>
      {/* Hiring Compliance Modal */}
      <HiringComplianceModal 
        open={showHiringModal} 
        onAcknowledge={handleHiringAcknowledge} 
      />
      
      {/* Verification Required Modal */}
      <VerificationRequiredModal
        open={showVerificationModal}
        onClose={() => setShowVerificationModal(false)}
        featureName="talent search"
      />
      
      <div className="space-y-8" data-testid="employer-search">
        {/* Header */}
        <div className="animate-fade-in">
          <h1 className="text-3xl font-bold text-[#0A0F1E]">Search Talent</h1>
          <p className="mt-2 text-[#5B6472]">Find verified candidates for your team.</p>
        </div>

        {/* Paywall */}
        {!isPaid && (
          <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl overflow-hidden">
            <div className="p-8 sm:p-12 text-center">
              <div className="w-20 h-20 rounded-full bg-[#1B5AFF]/10 flex items-center justify-center mx-auto mb-6">
                <Lock className="w-10 h-10 text-[#1B5AFF]" />
              </div>
              <h2 className="text-2xl sm:text-3xl font-bold text-[#0A0F1E] mb-3">Upgrade to access the full talent pool</h2>
              <p className="text-[#5B6472] mb-8 max-w-md mx-auto">Search verified candidates and send reach-outs to find your next great hire.</p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center max-w-md mx-auto">
                <Button
                  onClick={() => handleUpgrade('starter')}
                  variant="outline"
                  className="border-[#1B5AFF]/30 text-[#1B5AFF] hover:bg-[#1B5AFF]/10 rounded-xl px-6 py-6 text-base"
                  data-testid="paywall-starter-btn"
                >
                  Starter $299/mo
                </Button>
                <Button
                  onClick={() => handleUpgrade('growth')}
                  className="bg-[#1B5AFF] hover:bg-[#1B5AFF] text-[#FFFFFF] rounded-xl btn-glow px-6 py-6 text-base"
                  data-testid="paywall-growth-btn"
                >
                  Growth $799/mo
                </Button>
              </div>
            </div>
            {/* Blurred sample profiles */}
            <div className="p-6 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 filter blur-sm opacity-50 border-t border-[#E8EDF5]">
              {[1, 2, 3].map(i => (
                <Card key={i} className="bg-[#F5F8FF] border border-[#E8EDF5] p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-[#F5F8FF]" />
                    <div>
                      <div className="h-4 w-24 bg-[#F5F8FF] rounded mb-2" />
                      <div className="h-3 w-16 bg-[#F5F8FF] rounded" />
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </Card>
        )}

        {isPaid && (
          <>
            {/* VETT ID Quick Search */}
            <Card className="bg-gradient-to-r from-[#1B5AFF]/10 to-transparent border border-[#1B5AFF]/30 rounded-xl p-4 animate-fade-in">
              <div className="flex flex-col sm:flex-row items-center gap-4">
                <div className="flex items-center gap-2">
                  <div className="w-10 h-10 rounded-xl bg-[#1B5AFF]/20 flex items-center justify-center">
                    <Shield className="w-5 h-5 text-[#1B5AFF]" />
                  </div>
                  <div>
                    <p className="text-[#0A0F1E] font-medium">Search by VETT ID</p>
                    <p className="text-[#5B6472] text-xs">Enter a candidate&apos;s unique ID for direct lookup</p>
                  </div>
                </div>
                <div className="flex-1 flex gap-2 w-full sm:w-auto">
                  <Input
                    value={vettIdSearch}
                    onChange={(e) => setVettIdSearch(e.target.value.toUpperCase())}
                    placeholder="VETT-XXXXXX"
                    className="bg-[#F5F8FF]/50 border-[#E8EDF5] text-[#0A0F1E] rounded-xl h-10 font-mono"
                    data-testid="vett-id-search-input"
                    onKeyDown={(e) => e.key === 'Enter' && handleVettIdSearch()}
                  />
                  <Button
                    onClick={handleVettIdSearch}
                    disabled={searching}
                    className="bg-[#1B5AFF] hover:bg-[#1B5AFF] text-[#FFFFFF] rounded-xl h-10 px-6"
                    data-testid="vett-id-search-btn"
                  >
                    {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Find'}
                  </Button>
                </div>
              </div>
            </Card>

            {/* Search & Filters */}
            <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-6 animate-fade-in">
              <div className="grid lg:grid-cols-4 gap-4">
                {/* Keyword Search */}
                <div className="lg:col-span-2 space-y-2">
                  <Label className="text-[#0A0F1E]">Search</Label>
                  <div className="relative">
                    <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-[#5B6472]" />
                    <Input
                      value={filters.keyword}
                      onChange={(e) => setFilters(prev => ({ ...prev, keyword: e.target.value }))}
                      placeholder="Job title, skills, keywords..."
                      className="pl-10 bg-[#F5F8FF]/50 border-[#E8EDF5] text-[#0A0F1E] rounded-xl h-12"
                      data-testid="search-keyword"
                    />
                  </div>
                </div>

                {/* Industry */}
                <div className="space-y-2">
                  <Label className="text-[#0A0F1E]">Industry</Label>
                  <Select value={filters.industry || "all"} onValueChange={(v) => setFilters(prev => ({ ...prev, industry: v === "all" ? "" : v }))}>
                    <SelectTrigger className="bg-[#F5F8FF]/50 border-[#E8EDF5] text-[#0A0F1E] rounded-xl h-12">
                      <SelectValue placeholder="All industries" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#FFFFFF] border-[#E8EDF5]">
                      <SelectItem value="all" className="text-[#0A0F1E]">All industries</SelectItem>
                      {industries.map(industry => (
                        <SelectItem key={industry} value={industry} className="text-[#0A0F1E] hover:bg-[#F5F8FF]">
                          {industry}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Location */}
                <div className="space-y-2">
                  <Label className="text-[#0A0F1E]">Location</Label>
                  <Input
                    value={filters.location}
                    onChange={(e) => setFilters(prev => ({ ...prev, location: e.target.value }))}
                    placeholder="City, country..."
                    className="bg-[#F5F8FF]/50 border-[#E8EDF5] text-[#0A0F1E] rounded-xl h-12"
                  />
                </div>
              </div>

              {/* Additional Filters */}
              <div className="flex flex-wrap items-center gap-6 mt-6 pt-6 border-t border-[#E8EDF5]">
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="rehire_yes"
                    checked={filters.rehire_yes}
                    onCheckedChange={(checked) => setFilters(prev => ({ ...prev, rehire_yes: checked }))}
                  />
                  <Label htmlFor="rehire_yes" className="text-[#0A0F1E] text-sm cursor-pointer">Rehire: Yes</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="rehire_conditional"
                    checked={filters.rehire_conditional}
                    onCheckedChange={(checked) => setFilters(prev => ({ ...prev, rehire_conditional: checked }))}
                  />
                  <Label htmlFor="rehire_conditional" className="text-[#0A0F1E] text-sm cursor-pointer">Rehire: Conditional</Label>
                </div>
                <div className="flex items-center gap-3">
                  <Switch
                    id="open_only"
                    checked={filters.open_only}
                    onCheckedChange={(checked) => setFilters(prev => ({ ...prev, open_only: checked }))}
                  />
                  <Label htmlFor="open_only" className="text-[#0A0F1E] text-sm cursor-pointer">Open to opportunities</Label>
                </div>
              </div>

              {/* Tier Filter */}
              <div className="flex flex-wrap items-center gap-4 mt-4 pt-4 border-t border-[#E8EDF5]">
                <Label className="text-[#5B6472] text-sm">Filter by Tier:</Label>
                {[
                  { name: 'Basic', color: 'text-blue-400' },
                  { name: 'Verified', color: 'text-green-400' },
                  { name: 'Distinguished', color: 'text-purple-400' },
                  { name: 'Elite', color: 'text-amber-400' }
                ].map((tier) => (
                  <div key={tier.name} className="flex items-center gap-2">
                    <Checkbox
                      id={`tier_${tier.name}`}
                      checked={filters.tier_filter?.includes(tier.name.toLowerCase())}
                      onCheckedChange={(checked) => {
                        setFilters(prev => ({
                          ...prev,
                          tier_filter: checked 
                            ? [...(prev.tier_filter || []), tier.name.toLowerCase()]
                            : (prev.tier_filter || []).filter(t => t !== tier.name.toLowerCase())
                        }));
                      }}
                    />
                    <Label 
                      htmlFor={`tier_${tier.name}`} 
                      className={`text-sm cursor-pointer ${tier.color}`}
                    >
                      {tier.name}
                    </Label>
                  </div>
                ))}
                <Button
                  onClick={handleSearch}
                  disabled={searching}
                  className="ml-auto bg-[#1B5AFF] hover:bg-[#1B5AFF] text-[#FFFFFF] rounded-xl"
                  data-testid="search-btn"
                >
                  {searching ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <SearchIcon className="mr-2 h-4 w-4" />
                  )}
                  Search
                </Button>
              </div>
            </Card>

            {/* Results */}
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {results.map((candidate, i) => (
                <Card 
                  key={candidate.user_id} 
                  className={`bg-[#FFFFFF] border rounded-xl p-5 hover:border-[#1B5AFF]/50 transition-colors cursor-pointer animate-fade-in group ${
                    candidate.is_pro ? 'border-amber-500/30' : 'border-[#E8EDF5]'
                  }`}
                  style={{ animationDelay: `${i * 0.05}s` }}
                  onClick={() => handleViewTalent(candidate.user_id)}
                  data-testid={`candidate-card-${candidate.user_id}`}
                >
                  {/* Pro Badge - Hidden from search results per business requirement */}
                  
                  <div className="flex items-start gap-4">
                    {candidate.photo_url ? (
                      <img src={candidate.photo_url} alt="" className="w-14 h-14 rounded-full object-cover border border-[#E8EDF5]" />
                    ) : (
                      <div className="w-14 h-14 rounded-full bg-[#F5F8FF] border border-[#E8EDF5] flex items-center justify-center">
                        <User className="w-6 h-6 text-[#5B6472]" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-[#0A0F1E] truncate">
                        {candidate.first_name || candidate.last_name 
                          ? `${candidate.first_name} ${candidate.last_name}`.trim()
                          : 'Anonymous'}
                      </h3>
                      <p className="text-[#5B6472] text-sm truncate">{candidate.job_title || 'No title'}</p>
                      {candidate.location && (
                        <p className="text-[#5B6472] text-xs flex items-center gap-1 mt-1">
                          <MapPin className="w-3 h-3" />
                          {candidate.location}
                        </p>
                      )}
                    </div>
                    <div className="flex flex-col items-end gap-1">
                      {candidate.open_to_opportunities && candidate.open_to_opportunities !== 'not_looking' && candidate.open_to_opportunities !== 'hidden' && (
                        <span className={`px-2 py-1 rounded-full text-xs ${
                          candidate.open_to_opportunities === 'open' ? 'badge-open' : 'badge-selective'
                        }`}>
                          {candidate.open_to_opportunities === 'open' ? 'Open' : 'Selective'}
                        </span>
                      )}
                      {/* VETT Tier Badge */}
                      {candidate.tier && candidate.tier !== 'NONE' && (
                        <div className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-xs ${
                          candidate.tier === 'ELITE' ? 'bg-amber-500/20 text-amber-400' :
                          candidate.tier === 'DISTINGUISHED' ? 'bg-purple-500/20 text-purple-400' :
                          candidate.tier === 'VERIFIED' ? 'bg-green-500/20 text-green-400' :
                          candidate.tier === 'BASIC' ? 'bg-blue-500/20 text-blue-400' :
                          'bg-gray-500/20 text-gray-400'
                        }`} title="VETT Tier" data-testid="candidate-tier-badge">
                          <BadgeCheck className="w-3 h-3" />
                          <span className="font-medium">{candidate.tier_name || candidate.tier}</span>
                        </div>
                      )}
                      {/* VETT Score Badge */}
                      {candidate.vett_score > 0 && (
                        <VettScoreBadge score={candidate.vett_score} size="sm" />
                      )}
                    </div>
                  </div>

                  {candidate.skills?.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-3">
                      {candidate.skills.slice(0, 3).map((skill, i) => (
                        <span key={i} className="px-2 py-0.5 rounded-full bg-[#F5F8FF] text-[#0A0F1E] text-xs border border-[#E8EDF5]">
                          {skill}
                        </span>
                      ))}
                      {candidate.skills.length > 3 && (
                        <span className="px-2 py-0.5 rounded-full bg-[#F5F8FF] text-[#5B6472] text-xs">
                          +{candidate.skills.length - 3}
                        </span>
                      )}
                    </div>
                  )}

                  <div className="flex items-center justify-between mt-4 pt-4 border-t border-[#E8EDF5]">
                    <div className="flex items-center gap-3">
                      <span className="text-[#5B6472] text-xs flex items-center gap-1">
                        <CheckCircle className="w-3 h-3 text-green-400" />
                        {candidate.verified_records_count || 0} verified
                      </span>
                      {candidate.avg_rating > 0 && (
                        <span className="text-[#5B6472] text-xs flex items-center gap-1">
                          <Star className="w-3 h-3 fill-amber-400 text-amber-400" />
                          {candidate.avg_rating}
                        </span>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (!isSelected(candidate.user_id) && !canAddMore) {
                            toast.error('Maximum 3 candidates can be compared');
                            return;
                          }
                          toggleCandidate({
                            employee_id: candidate.user_id,
                            name: `${candidate.first_name || ''} ${candidate.last_name || ''}`.trim() || 'Anonymous',
                            photo_url: candidate.photo_url,
                            job_title: candidate.job_title
                          });
                        }}
                        className={`h-8 px-2 text-xs ${
                          isSelected(candidate.user_id)
                            ? 'bg-[#5B8AF0]/20 text-[#5B8AF0] border border-[#5B8AF0]/30'
                            : 'text-[#5B6472] hover:text-[#5B8AF0] hover:bg-[#5B8AF0]/10'
                        }`}
                        data-testid={`compare-toggle-${candidate.user_id}`}
                      >
                        <GitCompare className="w-3 h-3 mr-1" />
                        {isSelected(candidate.user_id) ? 'Selected' : 'Compare'}
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={(e) => handleSaveCandidate(candidate.user_id, e)}
                        className="h-8 w-8 p-0 text-[#5B6472] hover:text-[#1B5AFF] hover:bg-[#1B5AFF]/10"
                      >
                        <Bookmark className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-8 w-8 p-0 text-[#5B6472] group-hover:text-[#1B5AFF] hover:bg-[#1B5AFF]/10"
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </Card>
              ))}
            </div>

            {results.length === 0 && (
              <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-8 text-center">
                <SearchIcon className="w-12 h-12 text-[#5B6472] mx-auto mb-4" />
                <h3 className="text-lg font-medium text-[#0A0F1E] mb-2">No results yet</h3>
                <p className="text-[#5B6472]">
                  Use the search filters above to find verified candidates.
                </p>
              </Card>
            )}
          </>
        )}
      </div>
    </>
  );

  return isFounderMode ? searchContent : <DashboardLayout>{searchContent}</DashboardLayout>;
};

export default Search;
